/**
 * Favicon generation utilities
 */

/**
 * Generate a favicon from an image URL
 * @param imageUrl - URL of the source image
 * @param size - Size of the favicon (default: 32)
 * @returns Promise<string> - Data URL of the generated favicon
 */
export const generateFavicon = async (imageUrl: string, size: number = 32): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    
    img.onload = () => {
      try {
        // Create canvas
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        
        // Set canvas size
        canvas.width = size;
        canvas.height = size;
        
        // Draw image to canvas (resized)
        ctx.drawImage(img, 0, 0, size, size);
        
        // Convert to data URL
        const dataUrl = canvas.toDataURL('image/png');
        resolve(dataUrl);
      } catch (error) {
        reject(error);
      }
    };
    
    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };
    
    img.src = imageUrl;
  });
};

/**
 * Set a default favicon
 */
export const setDefaultFavicon = (): void => {
  const defaultFavicon = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" fill="%233B82F6"/><text x="16" y="20" font-family="Arial" font-size="14" fill="white" text-anchor="middle">GH</text></svg>';
  updateFavicon(defaultFavicon);
};

/**
 * Update the page favicon
 * @param faviconUrl - URL or data URL of the favicon
 */
export const updateFavicon = (faviconUrl: string): void => {
  // Remove existing favicons
  const existingFavicons = document.querySelectorAll('link[rel*="icon"]');
  existingFavicons.forEach(favicon => favicon.remove());
  
  // Add new favicon
  const favicon = document.createElement('link');
  favicon.rel = 'icon';
  favicon.type = 'image/x-icon';
  favicon.href = faviconUrl;
  document.head.appendChild(favicon);
  
  // Add apple-touch-icon for mobile devices
  const appleTouchIcon = document.createElement('link');
  appleTouchIcon.rel = 'apple-touch-icon';
  appleTouchIcon.href = faviconUrl;
  document.head.appendChild(appleTouchIcon);
};

/**
 * Generate and set favicon from logo
 * @param logoUrl - URL of the logo image
 */
export const setFaviconFromLogo = async (logoUrl: string): Promise<void> => {
  try {
    // Use default favicon if no logo URL provided
    if (!logoUrl || logoUrl.trim() === '') {
      console.log('No logo URL provided, using default favicon');
      setDefaultFavicon();
      return;
    }
    
    // For data URLs or relative paths, try direct update first
    if (logoUrl.startsWith('data:') || logoUrl.startsWith('/')) {
      updateFavicon(logoUrl);
      return;
    }
    
    const faviconDataUrl = await generateFavicon(logoUrl, 32);
    updateFavicon(faviconDataUrl);
  } catch (error) {
    console.warn('Failed to generate favicon from logo:', error);
    // Fallback to using the logo directly if it's a valid URL
    if (logoUrl && (logoUrl.startsWith('http') || logoUrl.startsWith('data:') || logoUrl.startsWith('/'))) {
      try {
        updateFavicon(logoUrl);
      } catch (fallbackError) {
        console.warn('Fallback favicon update also failed:', fallbackError);
      }
    }
  }
};